import { Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  ShieldCheck,
  MapPin,
  CheckCircle2,
  Coins,
  Landmark,
  BadgeCheck,
  Timer,
  Banknote,
  HelpCircle,
} from "lucide-react";
import { BRAND, IMAGES, telLink, waLink } from "@/lib/brand";
import BrandStamp from "@/components/BrandStamp";
import usePageMeta from "@/hooks/usePageMeta";

const AREAS = [
  { name: "Gurgaon (Gurugram)", desc: "All sectors — DLF, Sushant Lok, Golf Course Road, Sohna Road" },
  { name: "Manesar", desc: "IMT Manesar, New Manesar, Sector 1–8" },
  { name: "Delhi NCR", desc: "South Delhi, West Delhi, Karol Bagh, Chandni Chowk" },
  { name: "Faridabad", desc: "NIT Faridabad, Sector 15–89, Old Faridabad" },
  { name: "Noida & Greater Noida", desc: "All sectors, Noida Extension, Yamuna Expressway" },
  { name: "Ghaziabad", desc: "Vaishali, Indirapuram, Kaushambi, Raj Nagar" },
  { name: "Sohna, Dharuhera, Rewari", desc: "Home visits on prior appointment" },
  { name: "Bhiwadi, Neemrana, Palwal", desc: "Home visits on prior appointment" },
];

const KEYWORDS = [
  "Cash for Gold Gurgaon",
  "Sell Gold Gurugram",
  "Best Rate for Gold Gurgaon",
  "Sona Bech Ne Ki Jagah",
  "Gold Buyer Near Me",
  "Sell Gold Coins Gurgaon",
  "Sell Gold Biscuit Gurgaon",
  "Sell Silver Gurugram",
  "Mortgaged Gold Buyback",
  "Girvi Sona Chudwaye",
  "Govt Approved Gold Valuer Gurgaon",
  "Sell Old Jewellery Gurgaon",
  "Cash for Gold Delhi NCR",
  "Sell Gold Manesar",
  "Sell Gold Faridabad",
  "Sell Gold Noida",
  "Gold Auction Rescue",
  "Sell Hallmark Gold",
  "22K Gold Buyer Gurgaon",
  "24K Gold Buyer Gurgaon",
];

const REASONS = [
  {
    icon: BadgeCheck,
    title: "Govt-Approved Valuers",
    body: "Officially licensed valuers issuing certificates accepted by banks, insurance companies and courts.",
  },
  {
    icon: Timer,
    title: "Cash in 30 Minutes",
    body: "Walk in with gold, walk out with cash — the fastest turnaround anywhere in North India.",
  },
  {
    icon: Coins,
    title: "Live Karat Testing",
    body: "XRF machine placed on your side of the counter — you see the exact purity reading in real time.",
  },
  {
    icon: Banknote,
    title: "Cash OR Bank Transfer",
    body: "Cash upto ₹2 lakh, NEFT/RTGS/IMPS for larger amounts. Whichever suits you.",
  },
  {
    icon: Landmark,
    title: "Bank Mortgage Settlement",
    body: "Pledged gold in a bank? We settle directly with the lender and pay you the balance.",
  },
  {
    icon: ShieldCheck,
    title: "No Manipulation Guarantee",
    body: "No hidden deductions, no weight tricks, no melting-loss surprises. Everything on paper.",
  },
];

const FAQ = [
  {
    q: "Where can I sell my gold at the best rate in Gurgaon?",
    a: "Top Rate operates two branches in Gurgaon — Shop No. 230, Jacubpura (near Chota Gurudwara) and Shop 256, Sapphire Mall, Sector 49. We are govt-approved gold valuers offering the best market rate for jewellery, coins, biscuits and scrap gold. Call +91 98101 07276 for today's live rate.",
  },
  {
    q: "How much cash can I get for my gold immediately?",
    a: "Payout is based on today's 24K market rate multiplied by the purity of your gold (22K, 18K, 14K, etc.) and the net weight after stone deductions. Cash upto ₹2 lakh is paid on-spot; larger amounts are transferred via bank in the same visit. Get an on-call estimate on WhatsApp — send us the weight and purity.",
  },
  {
    q: "What documents do I need to sell gold?",
    a: "As per RBI/Govt of India regulations, we need a valid photo ID (Aadhaar / PAN / Voter ID / Passport / Driving License) and PAN card for transactions above ₹2 lakh. That's it — no other paperwork.",
  },
  {
    q: "Do you buy mortgaged or pledged gold from banks?",
    a: "Yes. This is one of our specialist services. If your gold is pledged with SBI, HDFC, Muthoot, Manappuram, IIFL or any bank/NBFC — we buy it at the best market rate and settle the loan directly with the lender. Commission is as low as 2%. Read more on our Girvi Sona page.",
  },
  {
    q: "How is the gold purity tested?",
    a: "We use an XRF (X-Ray Fluorescence) machine which non-destructively tests karat purity in about 30 seconds — no scratching, no acid, no damage to your ornaments. The reading is displayed live on a screen you can see.",
  },
  {
    q: "Do you buy silver as well?",
    a: "Yes — silver ornaments, coins, bars and utensils are all accepted at the best 999-fine silver rate of the day.",
  },
  {
    q: "Do you offer home visits?",
    a: "Yes. For larger consignments, senior citizens, or customers outside Gurgaon (Manesar, Delhi, Faridabad, Noida, Ghaziabad, Sohna, Dharuhera, Rewari, Bhiwadi, Neemrana, Palwal), we send a certified valuer to your home with a calibrated scale and XRF tester. Prior appointment required — call +91 98101 07276.",
  },
  {
    q: "Is selling gold to Top Rate safe and confidential?",
    a: "Absolutely. We are govt-approved valuers with two physical branches in Gurgaon. Every transaction is recorded, receipted, and 100% confidential. No customer information is shared publicly — critical for our Mortgaged Gold Buyback clients.",
  },
];

export default function SellGoldGurgaonPage() {
  usePageMeta({
    title:
      "Sell Gold in Gurgaon — Best Rate, Instant Cash | Top Rate Gurugram, Manesar & Delhi",
    description:
      "Cash for gold in Gurgaon at the best rate. Top Rate — govt-approved valuers with two branches in Gurgaon serving Manesar, Delhi, Faridabad, Noida, Ghaziabad, Sohna & all North India. Cash or bank transfer in 30 minutes. Call +91 98101 07276.",
    keywords:
      "cash for gold Gurgaon, sell gold Gurugram, best rate for gold, gold buyer Gurgaon, sell gold Manesar, cash for gold Delhi, sell gold coins, sell gold biscuit, mortgaged gold buyback, girvi sona bank se chudwaye, govt approved gold valuer, hallmark gold buyer, 22K gold buyer, sell silver Gurgaon, sell gold near me, sona bech ne ki jagah",
    path: "/sell-gold-gurgaon",
  });

  return (
    <div>
      {/* HEADER */}
      <section className="bg-[#FAF6EC] tr-grain border-b border-[#E5DCC5]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 border border-[#641C24]/40 bg-[#FFFDF8] text-[#641C24] px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold">
                <ShieldCheck size={13} strokeWidth={2} />
                Cash for Gold · Gurgaon · Manesar · Delhi
              </div>
              <h1 className="font-display text-[#2D181A] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.0] mt-6">
                Sell Gold in Gurgaon at the{" "}
                <span className="italic text-[#B8860B]">Best Rate</span> — Cash
                in 30 Minutes.
              </h1>
              <p className="mt-6 text-base sm:text-lg text-[#4A3B3C] leading-relaxed max-w-2xl">
                Looking to sell gold jewellery, coins, biscuits or silver in
                Gurgaon, Manesar, Delhi or nearby areas? Top Rate is a
                govt-approved gold buyer offering the best market rate,
                transparent karat testing and instant cash or bank transfer —
                no manipulation, no hidden charges.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sellgold-hero-whatsapp"
                  className="inline-flex items-center justify-center gap-2 bg-[#641C24] hover:bg-[#4A151A] text-[#FFFDF8] px-7 py-4 text-sm font-semibold uppercase tracking-wider"
                >
                  <MessageCircle size={17} /> Get Today's Rate on WhatsApp
                </a>
                <a
                  href={telLink}
                  data-testid="sellgold-hero-call"
                  className="inline-flex items-center justify-center gap-2 border border-[#641C24]/60 hover:bg-[#641C24] hover:text-[#FFFDF8] text-[#641C24] px-7 py-4 text-sm font-semibold uppercase tracking-wider"
                >
                  <Phone size={17} /> Call {BRAND.phone}
                </a>
              </div>
              <ul className="mt-8 grid grid-cols-2 gap-y-2 gap-x-6 text-sm text-[#2D181A]">
                {[
                  "Cash in 30 minutes",
                  "Live karat testing",
                  "Govt-approved valuers",
                  "Bank transfer available",
                  "Only 2% commission on mortgage buyback",
                  "Serving all of North India",
                ].map((x) => (
                  <li key={x} className="flex items-start gap-2">
                    <CheckCircle2
                      size={16}
                      className="mt-0.5 text-[#641C24] shrink-0"
                    />
                    {x}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="relative w-full max-w-md mx-auto">
                <img
                  src={IMAGES.ornament1}
                  alt="Sell gold jewellery Gurgaon — best rate at Top Rate govt approved valuers"
                  loading="eager"
                  className="w-full h-80 object-cover shadow-[0_18px_44px_-14px_rgba(45,24,26,0.4)]"
                />
                <img
                  src={IMAGES.rupees500}
                  alt="₹500 notes cash payout for gold Gurgaon"
                  loading="eager"
                  className="absolute -bottom-8 -right-4 w-3/5 h-auto object-contain shadow-[0_16px_40px_-12px_rgba(45,24,26,0.5)] rotate-[6deg]"
                />
                <div className="absolute -top-5 -left-3 bg-[#641C24] text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                  Best Rate ⚡
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="gold"
        tagline={
          <>
            Cash for Gold · Gurgaon · Manesar · Delhi · Faridabad · Noida · All
            North India
          </>
        }
      />

      {/* WHY TOP RATE */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
              Why Top Rate is the Best Place to Sell Gold in Gurgaon
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
              Six reasons customers keep{" "}
              <span className="italic text-[#641C24]">coming back</span>.
            </h2>
          </div>
          <div className="lg:col-span-4 text-[#4A3B3C] text-base leading-relaxed">
            Ratings, referrals and repeat visits — earned by doing gold
            evaluation the honest way, on-spot, in front of you.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {REASONS.map((r) => (
            <div
              key={r.title}
              className="bg-white border border-[#E5DCC5] p-8 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="w-11 h-11 border border-[#D4AF37] flex items-center justify-center text-[#641C24]">
                <r.icon size={20} strokeWidth={1.6} />
              </div>
              <h3 className="font-display text-xl font-bold mt-5">
                {r.title}
              </h3>
              <p className="text-sm text-[#4A3B3C] leading-relaxed mt-3">
                {r.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={<>Top Rate · Sona Do Paisa Lo · Best Rate for Your Gold</>}
      />

      {/* HOW TO SELL */}
      <section className="bg-[#FAF6EC] tr-grain border-y border-[#E5DCC5]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
            How to Sell Gold in Gurgaon — Step-by-Step Guide
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] max-w-3xl">
            A simple 5-step process — from your ornament box to cash in your
            hand.
          </h2>

          <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
            {[
              {
                t: "Call or WhatsApp",
                d: `Send your item's weight & purity on ${BRAND.phone} for an on-call estimate.`,
              },
              {
                t: "Visit a Branch",
                d: "Walk into Jacubpura or Sapphire Mall Sector 49 with your gold + ID proof.",
              },
              {
                t: "Live Karat Test",
                d: "XRF machine tests purity in front of you. Zero damage to ornaments.",
              },
              {
                t: "Transparent Weighing",
                d: "Digital calibrated scale — you see the exact reading. No stone-weight tricks.",
              },
              {
                t: "Instant Payout",
                d: "Cash or bank transfer within 30 minutes — pick whichever you prefer.",
              },
            ].map((step, i) => (
              <li
                key={step.t}
                className="bg-white border border-[#E5DCC5] p-6 shadow-sm"
              >
                <div className="font-display text-5xl font-black text-[#D4AF37]/30 leading-none">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-display text-lg font-bold mt-2">
                  {step.t}
                </div>
                <p className="text-xs text-[#4A3B3C] leading-relaxed mt-2">
                  {step.d}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <BrandStamp
        variant="gold"
        tagline={<>Two Branches in Gurgaon · Home Visits Across North India</>}
      />

      {/* AREAS SERVED */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
              Areas We Serve — Cash for Gold Near You
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
              <span className="italic text-[#641C24]">Local buyer</span> —
              across Gurgaon, Manesar, Delhi &amp; North India.
            </h2>
          </div>
          <div className="lg:col-span-4 text-[#4A3B3C] text-base leading-relaxed">
            Visit either Gurgaon branch or book a certified home valuation
            from anywhere in the NCR belt.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {AREAS.map((a) => (
            <div
              key={a.name}
              className="bg-white border border-[#E5DCC5] p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-2 text-[#641C24]">
                <MapPin size={16} strokeWidth={1.8} />
                <div className="font-display text-lg font-bold">{a.name}</div>
              </div>
              <p className="text-xs text-[#4A3B3C] leading-relaxed mt-2">
                {a.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <BrandStamp
        variant="light"
        tagline={<>Trusted by Families Across Gurgaon &amp; Delhi NCR</>}
      />

      {/* FAQ */}
      <section className="bg-[#2D181A] text-[#FFFDF8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
            <div className="lg:col-span-8">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-2">
                <HelpCircle size={14} /> Frequently Asked Questions
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
                Everything you need to know about{" "}
                <span className="italic text-[#D4AF37]">
                  selling gold in Gurgaon
                </span>
                .
              </h2>
            </div>
            <div className="lg:col-span-4 text-[#FFFDF8]/75 text-base leading-relaxed">
              Still have questions? Call{" "}
              <a
                href={telLink}
                className="text-[#D4AF37] font-semibold underline underline-offset-4"
              >
                {BRAND.phone}
              </a>{" "}
              — our valuer will answer in English, Hindi or Punjabi.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {FAQ.map((f, i) => (
              <div
                key={i}
                data-testid={`faq-${i}`}
                className="border-l-2 border-[#D4AF37]/60 pl-6"
              >
                <h3 className="font-display text-lg lg:text-xl font-bold text-[#FFFDF8]">
                  {f.q}
                </h3>
                <p className="text-sm text-[#FFFDF8]/80 leading-relaxed mt-3">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related searches / keyword cloud for SEO */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
          People Also Search For
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-3 leading-tight max-w-3xl">
          Related services offered by Top Rate.
        </h2>
        <div className="flex flex-wrap gap-2 mt-8">
          {KEYWORDS.map((k) => (
            <span
              key={k}
              className="inline-block bg-[#FAF6EC] border border-[#E5DCC5] text-[#2D181A] px-4 py-2 text-xs uppercase tracking-[0.14em] font-semibold"
            >
              {k}
            </span>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/mortgaged-gold"
            className="inline-flex items-center gap-2 bg-[#641C24] text-[#FFFDF8] hover:bg-[#4A151A] px-6 py-3.5 text-xs font-semibold uppercase tracking-wider"
          >
            Mortgaged Gold Buyback →
          </Link>
          <Link
            to="/branches"
            className="inline-flex items-center gap-2 border border-[#641C24] text-[#641C24] hover:bg-[#641C24] hover:text-[#FFFDF8] px-6 py-3.5 text-xs font-semibold uppercase tracking-wider"
          >
            Find a Branch →
          </Link>
          <a
            href={telLink}
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#2D181A] hover:bg-[#B8860B] px-6 py-3.5 text-xs font-semibold uppercase tracking-wider"
          >
            <Phone size={14} /> Call {BRAND.phone}
          </a>
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={
          <>Top Rate · Sona Do Paisa Lo · The Best Cash-for-Gold Address</>
        }
      />
    </div>
  );
}
